import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  Car,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Fuel,
  LineChart,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Shield,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { getAllTotalEnergiesPos, createTotalEnergiesPo } from '../services/totalEnergiesPoService';
import {
  fetchAllTotalEnergiesInvoices,
  createTotalEnergiesInvoice,
  updateTotalEnergiesInvoice,
  deleteTotalEnergiesInvoice,
  calcInvoiceTotal,
  emptyTotalEnergiesInvoiceFields,
  formatInvoiceDateFromYm,
  invoiceDateToYm,
  getTotalEnergiesInvoiceErrorMessage,
} from '../services/totalEnergiesInvoicesService';
import FleetFuelInvoicesSection from '../components/fleet/FleetFuelInvoicesSection';
import {
  buildFuelInvoiceTrendData,
  getFleetFuelInvoiceProjectOptions,
  getFleetFuelInvoiceYearOptions
} from '../utils/fleetFuelInvoiceShared';
import './FuelReport.css';

const ITEMS_PER_PAGE = 10;

const INVOICE_PAYMENT_STATUSES = ['Pending', 'Processed', 'Paid'];

function paymentStatusCssKey(status) {
  const s = String(status || 'Pending').trim().toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'processed') return 'processed';
  return 'pending';
}

const TAB_OPTIONS = [
  { key: 'paga', label: 'TotalEnergies PO', icon: Car },
  { key: 'report', label: 'TotalEnergies Invoices', icon: Fuel },
  { key: 'fuelcar', label: 'Cash spent on fuel', icon: Wallet },
  { key: 'trend', label: 'Fuel trend', icon: LineChart },
  { key: 'deposit', label: 'Security deposit', icon: Shield },
];

const TOTAL_ENERGIES_PO_BASE_PROJECTS = [
  'ADMIN',
  'BTS',
  'FTTM',
  'REA2C',
  'HELIOS',
  'HEP IIB (Lot 8)',
  'HEP IIB (Lot 10)',
];

function poDisplayDateToInputValue(display) {
  if (!display || !String(display).trim()) return '';
  const s = String(display).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parts = s.split('/');
  if (parts.length !== 3) return '';
  const day = String(parts[0]).padStart(2, '0');
  const month = String(parts[1]).padStart(2, '0');
  const year = String(parts[2]);
  if (!/^\d{4}$/.test(year)) return '';
  return `${year}-${month}-${day}`;
}

function poInputValueToDisplayDate(iso) {
  if (!iso || !String(iso).trim()) return '';
  const parts = String(iso).trim().split('-');
  if (parts.length !== 3) return '';
  const [y, m, d] = parts;
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

const parseAmountInput = (raw) => {
  const cleaned = String(raw ?? '').replace(/,/g, '').trim();
  if (cleaned === '') return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const cellInputWidthCh = (value, minCh = 6) =>
  Math.max(minCh, String(value || '').length + 1);

const datePaidToInputValue = (stored) => {
  const s = String(stored ?? '').trim();
  if (!s || s === '-') return '';
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return `${slash[3]}-${slash[2].padStart(2, '0')}-${slash[1].padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
};

const inputValueToDatePaid = (iso) => {
  const s = String(iso ?? '').trim();
  if (!s) return '-';
  const parts = s.split('-');
  if (parts.length === 3) {
    return `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}/${parts[0]}`;
  }
  return s;
};

function getPageNumbers(totalPages, safePage) {
  const pageNumbers = [];
  const maxVisiblePages = 5;
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else if (safePage <= 3) {
    for (let i = 1; i <= 4; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  } else if (safePage >= totalPages - 2) {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  }
  return pageNumbers;
}

function FrPagination({ totalFiltered, safePage, totalPages, indexOfFirstItem, indexOfLastItem, onPageChange }) {
  if (totalFiltered <= 0) return null;

  const goToPage = (page) => {
    onPageChange(page);
    const wrapper = document.querySelector('.fr-table-wrapper');
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <div className="fr-pagination">
      <div className="fr-pagination__info">
        Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
      </div>
      <div className="fr-pagination__controls">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="fr-soft-btn h-8 w-8"
          onClick={() => safePage > 1 && goToPage(safePage - 1)}
          disabled={safePage === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="fr-pagination__numbers">
          {getPageNumbers(totalPages, safePage).map((pageNum, index) =>
            pageNum === '...' ? (
              <span key={`ellipsis-${index}`} className="fr-pagination__ellipsis">
                …
              </span>
            ) : (
              <button
                key={pageNum}
                type="button"
                className={`fr-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </button>
            )
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="fr-soft-btn h-8 w-8"
          onClick={() => safePage < totalPages && goToPage(safePage + 1)}
          disabled={safePage === totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function FuelReport() {
  const formatTZS = (amount) => {
    const safe = Number.isFinite(amount) ? amount : 0;
    return `TZS ${new Intl.NumberFormat('en-US').format(Math.round(safe))}`;
  };

  const formatNumber = (amount) => {
    const safe = Number.isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US').format(Math.round(safe));
  };

  const parseTZS = (value) => {
    if (value === null || value === undefined) return 0;
    const s = String(value);
    const numeric = s.replace(/[^0-9.]/g, '');
    const n = Number(numeric);
    return Number.isFinite(n) ? n : 0;
  };

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState('');
  const [invoiceAdding, setInvoiceAdding] = useState(false);
  const [invoiceSavingIds, setInvoiceSavingIds] = useState(() => new Set());
  const invoiceSaveTimers = useRef({});
  const invoiceSnapshotsRef = useRef({});

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    setInvoicesError('');
    try {
      const rows = await fetchAllTotalEnergiesInvoices();
      rows.forEach((row) => {
        invoiceSnapshotsRef.current[row.id] = row;
      });
      setInvoices(rows);
    } catch (e) {
      console.error(e);
      setInvoices([]);
      setInvoicesError(getTotalEnergiesInvoiceErrorMessage(e) || 'Could not load TotalEnergies invoices.');
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
    const timersRef = invoiceSaveTimers;
    return () => {
      Object.values(timersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
    };
  }, [loadInvoices]);

  const [securityDeposits, setSecurityDeposits] = useState([
    { id: 1, company: 'Sagemcom TZ', project: 'BTS', amountPaid: 51300000, datePaid: '15/01/2024', comment: 'Initial security deposit for BTS project' },
    { id: 2, company: 'Sagemcom SAS', project: 'REA2C', amountPaid: 45000000, datePaid: '20/02/2024', comment: 'Security deposit for REA2C project' },
    { id: 3, company: 'Sagemcom TZ', project: 'ADMIN', amountPaid: 32000000, datePaid: '10/03/2024', comment: 'Security deposit for ADMIN project' },
    { id: 4, company: 'Sagemcom SAS', project: 'FTTM', amountPaid: 43700000, datePaid: '05/04/2024', comment: 'Security deposit for FTTM project' },
    { id: 5, company: 'Sagemcom TZ', project: 'HELIOS', amountPaid: 28000000, datePaid: '12/05/2024', comment: 'Security deposit for HELIOS project' },
  ]);

  const [fuelCarSheet] = useState([
    { id: 1, month: 'January 2025', projectName: 'BTS', company: 'Sagemcom TZ', amount: 19876543 },
    { id: 2, month: 'January 2025', projectName: 'REA2C', company: 'Sagemcom SAS', amount: 15892456 },
    { id: 3, month: 'January 2025', projectName: 'ADMIN', company: 'Sagemcom TZ', amount: 17456234 },
    { id: 4, month: 'February 2025', projectName: 'BTS', company: 'Sagemcom TZ', amount: 16234567 },
    { id: 5, month: 'February 2025', projectName: 'FTTM', company: 'Sagemcom SAS', amount: 18892456 },
    { id: 6, month: 'February 2025', projectName: 'HELIOS', company: 'Sagemcom TZ', amount: 20145673 },
    { id: 7, month: 'March 2025', projectName: 'BTS', company: 'Sagemcom TZ', amount: 17456234 },
    { id: 8, month: 'March 2025', projectName: 'REA2C', company: 'Sagemcom SAS', amount: 19234812 },
    { id: 9, month: 'March 2025', projectName: 'ADMIN', company: 'Sagemcom TZ', amount: 18505249 },
    { id: 10, month: 'April 2025', projectName: 'BTS', company: 'Sagemcom TZ', amount: 18892456 },
    { id: 11, month: 'April 2025', projectName: 'FTTM', company: 'Sagemcom SAS', amount: 20145673 },
    { id: 12, month: 'May 2025', projectName: 'BTS', company: 'Sagemcom TZ', amount: 21656658 },
  ]);

  const [pagaSheet, setPagaSheet] = useState([]);
  const [pagaSheetLoading, setPagaSheetLoading] = useState(true);
  const [pagaSheetError, setPagaSheetError] = useState(null);
  const [savingPo, setSavingPo] = useState(false);
  const [showAddPoForm, setShowAddPoForm] = useState(false);
  const [amountFocusKey, setAmountFocusKey] = useState(null);
  const [addPoForm, setAddPoForm] = useState({
    projectName: '',
    sdNo: '',
    poNo: '',
    poDate: '',
    poAmount: '',
  });

  const loadPagaSheet = useCallback(async () => {
    setPagaSheetLoading(true);
    setPagaSheetError(null);
    try {
      const rows = await getAllTotalEnergiesPos();
      setPagaSheet(rows);
    } catch (e) {
      console.error(e);
      setPagaSheetError(e?.message || 'Could not load TotalEnergies PO from Appwrite.');
      setPagaSheet([]);
    } finally {
      setPagaSheetLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPagaSheet();
  }, [loadPagaSheet]);

  const depositSummary = useMemo(() => {
    const byCompany = {};
    securityDeposits.forEach((dep) => {
      byCompany[dep.company] = (byCompany[dep.company] || 0) + dep.amountPaid;
    });
    const total = securityDeposits.reduce((sum, dep) => sum + dep.amountPaid, 0);
    return { byCompany, total };
  }, [securityDeposits]);

  const totalEnergiesPoProjectOptions = useMemo(() => {
    const set = new Set(TOTAL_ENERGIES_PO_BASE_PROJECTS);
    invoices.forEach((inv) => {
      const p = inv.project != null ? String(inv.project).trim() : '';
      if (p) set.add(p);
    });
    pagaSheet.forEach((row) => {
      const p = row.projectName != null ? String(row.projectName).trim() : '';
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [invoices, pagaSheet]);

  const invoiceProjectOptions = useMemo(() => {
    const set = new Set();
    invoices.forEach((inv) => {
      const p = String(inv.project || '').trim();
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [invoices]);

  const invoiceMonthOptions = useMemo(() => {
    const map = new Map();
    invoices.forEach((inv) => {
      const ym = invoiceDateToYm(inv.invoiceDate);
      if (!ym) return;
      const label = String(inv.invoiceDate || '').trim() || formatInvoiceDateFromYm(ym);
      map.set(ym, label);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([ym, label]) => ({ ym, label }));
  }, [invoices]);

  const [activeTab, setActiveTab] = useState('trend');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterProject, setFilterProject] = useState('All');
  const [filterInvoiceMonth, setFilterInvoiceMonth] = useState('All');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('All');
  const [trendFilterProject, setTrendFilterProject] = useState('All');
  const [trendFilterYear, setTrendFilterYear] = useState('All');
  const [fleetFuelInvoices, setFleetFuelInvoices] = useState([]);

  const trendYearOptions = useMemo(
    () => getFleetFuelInvoiceYearOptions(fleetFuelInvoices),
    [fleetFuelInvoices]
  );

  const fleetFuelProjectOptions = useMemo(
    () => getFleetFuelInvoiceProjectOptions(fleetFuelInvoices),
    [fleetFuelInvoices]
  );

  const trendFiltersActive = trendFilterProject !== 'All' || trendFilterYear !== 'All';

  const fuelTrendData = useMemo(
    () =>
      buildFuelInvoiceTrendData(fleetFuelInvoices, {
        projectFilter: trendFilterProject,
        year: trendFilterYear
      }),
    [fleetFuelInvoices, trendFilterProject, trendFilterYear]
  );

  const trendSummary = useMemo(() => {
    if (fuelTrendData.length === 0) {
      return {
        totalAmount: 0,
        averageMonthly: 0,
        peakLabel: '—',
        peakAmount: 0,
        invoiceCount: 0,
        monthCount: 0
      };
    }
    const totalAmount = fuelTrendData.reduce((sum, row) => sum + row.cost, 0);
    const invoiceCount = fuelTrendData.reduce((sum, row) => sum + row.invoiceCount, 0);
    const peak = fuelTrendData.reduce(
      (best, row) => (row.cost > best.cost ? row : best),
      fuelTrendData[0]
    );
    return {
      totalAmount,
      averageMonthly: totalAmount / fuelTrendData.length,
      peakLabel: peak.label,
      peakAmount: peak.cost,
      invoiceCount,
      monthCount: fuelTrendData.length
    };
  }, [fuelTrendData]);

  const clearTrendFilters = () => {
    setTrendFilterProject('All');
    setTrendFilterYear('All');
  };

  const invoiceFiltersActive =
    filterProject !== 'All' || filterInvoiceMonth !== 'All' || filterPaymentStatus !== 'All';

  const filteredInvoices = useMemo(
    () =>
      invoices.filter((inv) => {
        if (filterProject !== 'All' && String(inv.project || '').trim() !== filterProject) {
          return false;
        }
        if (filterPaymentStatus !== 'All' && inv.paymentStatus !== filterPaymentStatus) {
          return false;
        }
        if (filterInvoiceMonth !== 'All') {
          const ym = inv.invoiceMonthYm || invoiceDateToYm(inv.invoiceDate);
          if (ym !== filterInvoiceMonth) return false;
        }
        return true;
      }),
    [invoices, filterProject, filterInvoiceMonth, filterPaymentStatus]
  );

  const invoiceSummary = useMemo(() => {
    const source = filteredInvoices;
    const totalAmount = source.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const paidInvoices = source.filter((inv) => inv.paymentStatus === 'Paid');
    const processedInvoices = source.filter((inv) => inv.paymentStatus === 'Processed');
    const pendingInvoices = source.filter((inv) => inv.paymentStatus === 'Pending');
    const paidAmount = paidInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const processedAmount = processedInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);

    return {
      totalAmount,
      paidCount: paidInvoices.length,
      paidAmount,
      processedCount: processedInvoices.length,
      processedAmount,
      pendingCount: pendingInvoices.length,
      pendingAmount,
      totalCount: source.length,
    };
  }, [filteredInvoices]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterProject, filterInvoiceMonth, filterPaymentStatus]);

  const paginatedSource = useMemo(() => {
    if (activeTab === 'report') return filteredInvoices;
    if (activeTab === 'paga') return pagaSheet;
    if (activeTab === 'fuelcar') return fuelCarSheet;
    if (activeTab === 'deposit') return securityDeposits;
    return [];
  }, [activeTab, filteredInvoices, pagaSheet, fuelCarSheet, securityDeposits]);

  const totalFiltered = paginatedSource.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const indexOfLastItem = Math.min(safePage * ITEMS_PER_PAGE, totalFiltered);
  const paginatedItems = useMemo(
    () => paginatedSource.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE),
    [paginatedSource, safePage]
  );

  const paginatedInvoices = activeTab === 'report' ? paginatedItems : [];
  const paginatedPaga = activeTab === 'paga' ? paginatedItems : [];
  const paginatedFuelCar = activeTab === 'fuelcar' ? paginatedItems : [];
  const paginatedDeposits = activeTab === 'deposit' ? paginatedItems : [];

  const persistInvoiceRow = useCallback(async (id) => {
    const row = invoiceSnapshotsRef.current[id];
    if (!row) return;

    setInvoiceSavingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setInvoicesError('');

    try {
      const updated = await updateTotalEnergiesInvoice(id, row);
      invoiceSnapshotsRef.current[id] = updated;
      setInvoices((prev) => prev.map((inv) => (inv.id === id ? updated : inv)));
    } catch (err) {
      console.error(err);
      setInvoicesError(getTotalEnergiesInvoiceErrorMessage(err) || 'Failed to save invoice.');
    } finally {
      setInvoiceSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const scheduleInvoiceSave = useCallback(
    (id) => {
      const timers = invoiceSaveTimers.current;
      if (timers[id]) window.clearTimeout(timers[id]);
      timers[id] = window.setTimeout(() => {
        delete timers[id];
        persistInvoiceRow(id);
      }, 600);
    },
    [persistInvoiceRow]
  );

  const updateInvoiceRow = useCallback(
    (id, patch) => {
      setInvoices((prev) => {
        const current = prev.find((inv) => inv.id === id);
        if (!current) return prev;

        const merged = { ...current, ...patch };
        if ('fuelAmount' in patch || 'managementFee' in patch || 'vat' in patch) {
          merged.totalAmount = calcInvoiceTotal(
            merged.fuelAmount,
            merged.managementFee,
            merged.vat
          );
        }
        if ('paymentStatus' in patch && patch.paymentStatus !== 'Paid') {
          merged.datePaid = '-';
        }
        if ('invoiceDate' in patch) {
          merged.invoiceMonthYm = invoiceDateToYm(merged.invoiceDate);
        }

        invoiceSnapshotsRef.current[id] = merged;
        scheduleInvoiceSave(id);

        return prev.map((inv) => (inv.id === id ? merged : inv));
      });
    },
    [scheduleInvoiceSave]
  );

  const handleAddRow = async () => {
    setInvoiceAdding(true);
    setInvoicesError('');
    try {
      const minSort = invoices.reduce((min, row) => Math.min(min, Number(row.sortOrder) || 0), 0);
      const created = await createTotalEnergiesInvoice({
        ...emptyTotalEnergiesInvoiceFields(),
        sortOrder: minSort - 10,
      });
      invoiceSnapshotsRef.current[created.id] = created;
      setInvoices((prev) => [created, ...prev]);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setInvoicesError(getTotalEnergiesInvoiceErrorMessage(err) || 'Failed to add invoice row.');
    } finally {
      setInvoiceAdding(false);
    }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Delete this invoice row?')) return;

    const timers = invoiceSaveTimers.current;
    if (timers[id]) {
      window.clearTimeout(timers[id]);
      delete timers[id];
    }
    delete invoiceSnapshotsRef.current[id];

    try {
      await deleteTotalEnergiesInvoice(id);
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
    } catch (err) {
      console.error(err);
      setInvoicesError(getTotalEnergiesInvoiceErrorMessage(err) || 'Failed to delete invoice row.');
    }
  };

  const clearInvoiceFilters = () => {
    setFilterProject('All');
    setFilterInvoiceMonth('All');
    setFilterPaymentStatus('All');
  };

  const [showDepositEditModal, setShowDepositEditModal] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [depositEditForm, setDepositEditForm] = useState({ comment: '' });

  const handleOpenDepositEditModal = (deposit) => {
    setEditingDeposit(deposit);
    setDepositEditForm({ comment: deposit.comment });
    setShowDepositEditModal(true);
  };

  const handleCloseDepositEditModal = () => {
    setShowDepositEditModal(false);
    setEditingDeposit(null);
    setDepositEditForm({ comment: '' });
  };

  const handleDepositEditFormChange = (field, value) => {
    setDepositEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddPoFormChange = (field, value) => {
    setAddPoForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveNewPo = async () => {
    const requiredFields = ['projectName', 'sdNo', 'poNo', 'poDate', 'poAmount'];
    const missing = requiredFields.some((field) => !String(addPoForm[field] || '').trim());
    if (missing) {
      alert('Please fill all PO fields.');
      return;
    }

    const poAmount = parseTZS(addPoForm.poAmount);
    const amountInvoiced = 0;
    const poBalance = Math.max(0, poAmount - amountInvoiced);

    setSavingPo(true);
    try {
      const created = await createTotalEnergiesPo({
        projectName: addPoForm.projectName.trim(),
        sdNo: addPoForm.sdNo.trim(),
        poNo: addPoForm.poNo.trim(),
        poDate: addPoForm.poDate.trim(),
        poAmount,
        amountInvoiced,
        poBalance,
      });
      setPagaSheet((prev) => [created, ...prev]);
      setAddPoForm({
        projectName: '',
        sdNo: '',
        poNo: '',
        poDate: '',
        poAmount: '',
      });
      setShowAddPoForm(false);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to save PO. Run npm run setup:total-energies-po if the collection is missing.');
    } finally {
      setSavingPo(false);
    }
  };

  const handleCloseAddPoModal = () => {
    setShowAddPoForm(false);
  };

  const addPoBalancePreview = parseTZS(addPoForm.poAmount);

  const handleSaveDeposit = () => {
    if (!editingDeposit) return;

    const updatedDeposits = securityDeposits.map((dep) => {
      if (dep.id === editingDeposit.id) {
        return { ...dep, comment: depositEditForm.comment };
      }
      return dep;
    });

    setSecurityDeposits(updatedDeposits);
    alert('Security deposit comment has been updated successfully!');
    handleCloseDepositEditModal();
  };

  const trendSummaryCards = [
    {
      id: 'total',
      label: `Total invoiced${trendFiltersActive ? ' · filtered' : ''}`,
      value: formatTZS(trendSummary.totalAmount),
      meta: `${trendSummary.invoiceCount} invoices · ${trendSummary.monthCount} months`,
      accent: '#3b82f6',
      icon: Fuel,
    },
    {
      id: 'average',
      label: 'Average per month',
      value: formatTZS(trendSummary.averageMonthly),
      meta: trendSummary.monthCount > 0 ? 'Across selected months' : 'No months yet',
      accent: '#8b5cf6',
      icon: LineChart,
    },
    {
      id: 'peak',
      label: 'Peak month',
      value: formatTZS(trendSummary.peakAmount),
      meta: trendSummary.peakLabel,
      accent: '#f59e0b',
      icon: Wallet,
    },
  ];

  const invoiceSummaryCards = [
    {
      id: 'total',
      label: `Total invoices amount${invoiceFiltersActive ? ' · filtered' : ''}`,
      value: formatTZS(invoiceSummary.totalAmount),
      meta: `${invoiceSummary.totalCount} invoices`,
      accent: '#3b82f6',
      icon: Fuel,
    },
    {
      id: 'paid',
      label: `Paid${invoiceFiltersActive ? ' · filtered' : ''}`,
      value: formatTZS(invoiceSummary.paidAmount),
      meta: `${invoiceSummary.paidCount} invoices`,
      accent: '#10b981',
      icon: CheckCircle2,
    },
    {
      id: 'processed',
      label: `Processed${invoiceFiltersActive ? ' · filtered' : ''}`,
      value: formatTZS(invoiceSummary.processedAmount),
      meta: `${invoiceSummary.processedCount} invoices`,
      accent: '#8b5cf6',
      icon: Loader2,
    },
    {
      id: 'pending',
      label: `Pending${invoiceFiltersActive ? ' · filtered' : ''}`,
      value: formatTZS(invoiceSummary.pendingAmount),
      meta: `${invoiceSummary.pendingCount} invoices`,
      accent: '#f59e0b',
      icon: Wallet,
    },
  ];

  return (
    <div className="fr-page">
      <div className="fr-scroll">
        <div className="fr-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Fleet', to: '/sagemcom/fleet-management', hideOnMobile: true },
              { label: 'Fuel report' },
            ]}
          />

          <section className="fr-intro" aria-label="Fuel report overview">
            <div className="fr-intro-text">
              <h1 className="fr-intro-title">Fuel report</h1>
              <p className="fr-intro-subtitle">
                TotalEnergies fuel tracking — POs, invoices, cash spend, trends, and security deposits
              </p>
            </div>
          </section>

          <div className="fr-lot-tabs" role="tablist" aria-label="Fuel report views">
            {TAB_OPTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                className={`fr-lot-tabs__btn${activeTab === key ? ' is-active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'trend' && (
            <>
              <div className="fr-summary-cards">
                {trendSummaryCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div key={card.id} className="fr-summary-card" style={{ '--cat-accent': card.accent }}>
                      <div className="fr-summary-card__top">
                        <span className="fr-summary-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="fr-summary-card__label">{card.label}</span>
                      </div>
                      <div className="fr-summary-card__count">{card.meta}</div>
                      <div className="fr-summary-card__amount">{card.value}</div>
                    </div>
                  );
                })}
              </div>

              <div className="fr-table-section">
                <div className="fr-table-section__head">
                  <div>
                    <h2>Fuel invoice trend</h2>
                    <p className="fr-table-section__hint">
                      Same chart and fuel invoice spreadsheet as Fleet Dashboard — monthly total invoice
                      amount by month and year.
                    </p>
                  </div>
                </div>

                <div className="fr-toolbar">
                  <select
                    className="fr-filter-select"
                    value={trendFilterProject}
                    onChange={(e) => setTrendFilterProject(e.target.value)}
                    aria-label="Trend project"
                  >
                    <option value="All">All projects</option>
                    {fleetFuelProjectOptions.map((project) => (
                      <option key={project} value={project}>
                        {project}
                      </option>
                    ))}
                  </select>
                  <select
                    className="fr-filter-select"
                    value={trendFilterYear}
                    onChange={(e) => setTrendFilterYear(e.target.value)}
                    aria-label="Trend year"
                  >
                    <option value="All">All years</option>
                    {trendYearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <div className="fr-toolbar__spacer" aria-hidden="true" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="fr-soft-btn h-9 gap-1.5"
                    onClick={clearTrendFilters}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Clear
                  </Button>
                  {trendFiltersActive ? (
                    <span className="fr-filter-count">
                      {fuelTrendData.length} month{fuelTrendData.length === 1 ? '' : 's'} shown
                    </span>
                  ) : null}
                </div>

                <FleetFuelInvoicesSection
                  projectFilter={trendFilterProject}
                  yearFilter={trendFilterYear}
                  onRowsChange={setFleetFuelInvoices}
                />
              </div>
            </>
          )}

          {activeTab === 'report' && (
            <>
              <div className="fr-summary-cards">
                {invoiceSummaryCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div key={card.id} className="fr-summary-card" style={{ '--cat-accent': card.accent }}>
                      <div className="fr-summary-card__top">
                        <span className="fr-summary-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="fr-summary-card__label">{card.label}</span>
                      </div>
                      <div className="fr-summary-card__count">{card.meta}</div>
                      <div className="fr-summary-card__amount">{card.value}</div>
                    </div>
                  );
                })}
              </div>

              <div className="fr-table-section">
                <div className="fr-table-section__head">
                  <div>
                    <h2>
                      Invoice details
                      <span className="fr-table-section__count">{filteredInvoices.length}</span>
                    </h2>
                    <p className="fr-table-section__hint">
                      Click any cell to edit. Changes save automatically. Use Add row for a new invoice.
                    </p>
                  </div>
                  <Button
                    type="button"
                    className="fr-create-btn h-9 gap-1.5"
                    onClick={handleAddRow}
                    disabled={invoiceAdding || invoicesLoading}
                  >
                    <Plus className="h-4 w-4" />
                    {invoiceAdding ? 'Adding…' : 'Add row'}
                  </Button>
                </div>
                {invoicesError ? (
                  <div className="fr-error" role="alert">
                    {invoicesError}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="fr-soft-btn ml-2 h-8 gap-1.5"
                      onClick={loadInvoices}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Retry
                    </Button>
                  </div>
                ) : null}
                <div className="fr-toolbar">
                  <select
                    className="fr-filter-select"
                    value={filterProject}
                    onChange={(e) => setFilterProject(e.target.value)}
                    aria-label="Project"
                  >
                    <option value="All">All projects</option>
                    {invoiceProjectOptions.map((project) => (
                      <option key={project} value={project}>
                        {project}
                      </option>
                    ))}
                  </select>
                  <select
                    className="fr-filter-select"
                    value={filterInvoiceMonth}
                    onChange={(e) => setFilterInvoiceMonth(e.target.value)}
                    aria-label="Invoice date"
                  >
                    <option value="All">All invoice dates</option>
                    {invoiceMonthOptions.map(({ ym, label }) => (
                      <option key={ym} value={ym}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="fr-filter-select"
                    value={filterPaymentStatus}
                    onChange={(e) => setFilterPaymentStatus(e.target.value)}
                    aria-label="Payment status"
                  >
                    <option value="All">All statuses</option>
                    {INVOICE_PAYMENT_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <div className="fr-toolbar__spacer" aria-hidden="true" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="fr-soft-btn h-9 gap-1.5"
                    onClick={clearInvoiceFilters}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Clear
                  </Button>
                  {invoiceFiltersActive ? (
                    <span className="fr-filter-count">
                      {filteredInvoices.length} of {invoices.length} invoices shown
                    </span>
                  ) : null}
                </div>
                <datalist id="fr-te-po-options">
                  {pagaSheet.map((row) => (
                    <option key={row.id || row.poNo} value={row.poNo} />
                  ))}
                </datalist>
                <div className="fr-table-shell">
                  <div className="fr-table-wrapper">
                    <table className="fr-table fr-table--editable">
                      <thead>
                        <tr>
                          <th>Invoice No</th>
                          <th>Invoice Date</th>
                          <th>Project</th>
                          <th>Fuel amount</th>
                          <th>Management fee</th>
                          <th>VAT</th>
                          <th>Total invoice amount</th>
                          <th>PO</th>
                          <th>Payment status</th>
                          <th>Date paid</th>
                          <th>Comment</th>
                          <th aria-label="Delete" />
                        </tr>
                      </thead>
                      <tbody>
                        {invoicesLoading ? (
                          <tr>
                            <td colSpan={12} className="fr-empty-row">
                              Loading TotalEnergies invoices…
                            </td>
                          </tr>
                        ) : paginatedInvoices.length === 0 ? (
                          <tr>
                            <td colSpan={12} className="fr-empty-row">
                              {invoices.length === 0 ? (
                                <>No invoices yet. Click &quot;Add row&quot; to create one.</>
                              ) : (
                                <>No invoices match the current filters. Try adjusting or clearing filters.</>
                              )}
                            </td>
                          </tr>
                        ) : (
                          paginatedInvoices.map((invoice) => {
                            const saving = invoiceSavingIds.has(invoice.id);
                            return (
                            <tr key={invoice.id} className={saving ? 'fr-row-saving' : undefined}>
                              <td>
                                <input
                                  className="fr-cell-input fr-cell-invoice-no"
                                  type="text"
                                  value={invoice.invoiceNo || ''}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, { invoiceNo: e.target.value })
                                  }
                                  placeholder="Invoice No"
                                />
                              </td>
                              <td>
                                <input
                                  className="fr-cell-input fr-cell-month"
                                  type="month"
                                  value={invoiceDateToYm(invoice.invoiceDate)}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, {
                                      invoiceDate: formatInvoiceDateFromYm(e.target.value),
                                    })
                                  }
                                  aria-label="Invoice month"
                                />
                              </td>
                              <td className="fr-project-cell">
                                <select
                                  className="fr-cell-select fr-cell-project"
                                  value={invoice.project || ''}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, { project: e.target.value })
                                  }
                                  style={{
                                    width: `${cellInputWidthCh(invoice.project || 'Select project', 12)}ch`,
                                  }}
                                  title={invoice.project || 'Select project'}
                                >
                                  <option value="">Select project</option>
                                  {totalEnergiesPoProjectOptions.map((p) => (
                                    <option key={p} value={p}>
                                      {p}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="fr-value">
                                <input
                                  className="fr-cell-input fr-cell-amount"
                                  value={
                                    amountFocusKey === `${invoice.id}-fuel`
                                      ? String(invoice.fuelAmount === 0 ? '' : invoice.fuelAmount)
                                      : formatNumber(invoice.fuelAmount)
                                  }
                                  onFocus={() => setAmountFocusKey(`${invoice.id}-fuel`)}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, {
                                      fuelAmount: parseAmountInput(e.target.value),
                                    })
                                  }
                                  onBlur={() => setAmountFocusKey(null)}
                                />
                              </td>
                              <td className="fr-value">
                                <input
                                  className="fr-cell-input fr-cell-amount"
                                  value={
                                    amountFocusKey === `${invoice.id}-fee`
                                      ? String(invoice.managementFee === 0 ? '' : invoice.managementFee)
                                      : formatNumber(invoice.managementFee)
                                  }
                                  onFocus={() => setAmountFocusKey(`${invoice.id}-fee`)}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, {
                                      managementFee: parseAmountInput(e.target.value),
                                    })
                                  }
                                  onBlur={() => setAmountFocusKey(null)}
                                />
                              </td>
                              <td className="fr-value">
                                <input
                                  className="fr-cell-input fr-cell-amount"
                                  value={
                                    amountFocusKey === `${invoice.id}-vat`
                                      ? String(invoice.vat === 0 ? '' : invoice.vat)
                                      : formatNumber(invoice.vat)
                                  }
                                  onFocus={() => setAmountFocusKey(`${invoice.id}-vat`)}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, {
                                      vat: parseAmountInput(e.target.value),
                                    })
                                  }
                                  onBlur={() => setAmountFocusKey(null)}
                                />
                              </td>
                              <td className="fr-value fr-cell-total">
                                TSh {formatNumber(invoice.totalAmount)}
                              </td>
                              <td className="fr-po-cell">
                                <input
                                  className="fr-cell-input fr-cell-po"
                                  type="text"
                                  value={invoice.po || ''}
                                  list="fr-te-po-options"
                                  onChange={(e) => updateInvoiceRow(invoice.id, { po: e.target.value })}
                                  placeholder="PO"
                                  style={{ width: `${cellInputWidthCh(invoice.po)}ch` }}
                                  title={invoice.po || ''}
                                />
                              </td>
                              <td>
                                <select
                                  className={`fr-cell-select fr-cell-status fr-status-${paymentStatusCssKey(invoice.paymentStatus)}`}
                                  value={invoice.paymentStatus || 'Pending'}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, { paymentStatus: e.target.value })
                                  }
                                >
                                  {INVOICE_PAYMENT_STATUSES.map((status) => (
                                    <option key={status} value={status}>
                                      {status}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="date"
                                  className={`fr-cell-input fr-cell-date${invoice.paymentStatus === 'Paid' ? ' fr-date-paid--paid' : ''}`}
                                  value={datePaidToInputValue(invoice.datePaid)}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, {
                                      datePaid: inputValueToDatePaid(e.target.value),
                                    })
                                  }
                                  disabled={invoice.paymentStatus !== 'Paid'}
                                  aria-label="Date paid"
                                />
                              </td>
                              <td className="fr-comment">
                                <input
                                  className="fr-cell-input fr-cell-comment"
                                  value={invoice.comment || ''}
                                  onChange={(e) =>
                                    updateInvoiceRow(invoice.id, { comment: e.target.value })
                                  }
                                  placeholder="Notes…"
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="fr-delete-btn"
                                  onClick={() => handleDeleteRow(invoice.id)}
                                  aria-label="Delete row"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <FrPagination
                  totalFiltered={totalFiltered}
                  safePage={safePage}
                  totalPages={totalPages}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}

          {activeTab === 'paga' && (
            <div className="fr-table-section">
              <div className="fr-table-section__head">
                <h2>
                  TotalEnergies PO
                  <span className="fr-table-section__count">{pagaSheet.length}</span>
                </h2>
                <Button type="button" className="fr-create-btn h-9 gap-1.5" onClick={() => setShowAddPoForm(true)}>
                  <Plus className="h-4 w-4" />
                  Add PO
                </Button>
              </div>

              {pagaSheetError ? (
                <div className="fr-error" role="alert">
                  {pagaSheetError}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="fr-soft-btn ml-2 h-8 gap-1.5"
                    onClick={loadPagaSheet}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                </div>
              ) : null}

              <div className="fr-table-shell">
                <div className="fr-table-wrapper">
                  <table className="fr-table">
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>SD NO</th>
                        <th>PO NO</th>
                        <th>PO Date</th>
                        <th>PO Amount</th>
                        <th>Amount invoiced</th>
                        <th>PO Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagaSheetLoading ? (
                        <tr>
                          <td colSpan={7} className="fr-empty-row">
                            Loading TotalEnergies PO…
                          </td>
                        </tr>
                      ) : pagaSheet.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="fr-empty-row">
                            No TotalEnergies PO rows yet. Use Add PO to create one in Appwrite.
                          </td>
                        </tr>
                      ) : (
                        paginatedPaga.map((item) => (
                          <tr key={item.id || item.documentId}>
                            <td>{item.projectName}</td>
                            <td>{item.sdNo}</td>
                            <td>{item.poNo}</td>
                            <td>{item.poDate}</td>
                            <td className="fr-value">TZS {formatNumber(item.poAmount)}</td>
                            <td className="fr-value">TZS {formatNumber(item.amountInvoiced)}</td>
                            <td className="fr-value">TZS {formatNumber(item.poBalance)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {!pagaSheetLoading && totalFiltered > 0 ? (
                <FrPagination
                  totalFiltered={totalFiltered}
                  safePage={safePage}
                  totalPages={totalPages}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={setCurrentPage}
                />
              ) : null}
            </div>
          )}

          {activeTab === 'fuelcar' && (
            <div className="fr-table-section">
              <div className="fr-table-section__head">
                <h2>
                  Cash spent on fuel
                  <span className="fr-table-section__count">{fuelCarSheet.length}</span>
                </h2>
              </div>
              <div className="fr-table-shell">
                <div className="fr-table-wrapper">
                  <table className="fr-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Project Name</th>
                        <th>Company</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFuelCar.map((item) => (
                        <tr key={item.id}>
                          <td>{item.month}</td>
                          <td>{item.projectName}</td>
                          <td>{item.company}</td>
                          <td className="fr-value">TZS {formatNumber(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <FrPagination
                totalFiltered={totalFiltered}
                safePage={safePage}
                totalPages={totalPages}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={setCurrentPage}
              />
            </div>
          )}

          {activeTab === 'deposit' && (
            <>
              <div className="fr-summary-cards fr-summary-cards--deposit">
                <div className="fr-summary-card" style={{ '--cat-accent': '#3b82f6' }}>
                  <div className="fr-summary-card__top">
                    <span className="fr-summary-card__icon" aria-hidden>
                      <Shield className="h-4 w-4" />
                    </span>
                    <span className="fr-summary-card__label">Total security deposit</span>
                  </div>
                  <div className="fr-summary-card__count">{securityDeposits.length} transactions</div>
                  <div className="fr-summary-card__amount">{formatTZS(depositSummary.total)}</div>
                </div>
                {Object.entries(depositSummary.byCompany).map(([company, amount]) => (
                  <div key={company} className="fr-summary-card" style={{ '--cat-accent': '#8b5cf6' }}>
                    <div className="fr-summary-card__top">
                      <span className="fr-summary-card__icon" aria-hidden>
                        <Building2 className="h-4 w-4" />
                      </span>
                      <span className="fr-summary-card__label">{company}</span>
                    </div>
                    <div className="fr-summary-card__count">Aggregated</div>
                    <div className="fr-summary-card__amount">{formatTZS(amount)}</div>
                  </div>
                ))}
              </div>

              <div className="fr-table-section">
                <div className="fr-table-section__head">
                  <h2>
                    Security deposit transactions
                    <span className="fr-table-section__count">{securityDeposits.length}</span>
                  </h2>
                </div>
                <div className="fr-table-shell">
                  <div className="fr-table-wrapper">
                    <table className="fr-table">
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Project</th>
                          <th>Amount Paid</th>
                          <th>Date Paid</th>
                          <th>Comment</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDeposits.map((deposit) => (
                          <tr key={deposit.id}>
                            <td>{deposit.company}</td>
                            <td>{deposit.project}</td>
                            <td className="fr-value">TZS {formatNumber(deposit.amountPaid)}</td>
                            <td>{deposit.datePaid}</td>
                            <td className="fr-comment">{deposit.comment}</td>
                            <td>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="fr-soft-btn h-8 gap-1.5"
                                onClick={() => handleOpenDepositEditModal(deposit)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <FrPagination
                  totalFiltered={totalFiltered}
                  safePage={safePage}
                  totalPages={totalPages}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {showAddPoForm ? (
        <div className="fr-modal-overlay" onClick={handleCloseAddPoModal} role="presentation">
          <div
            className="fr-modal fr-modal--wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="fr-add-po-title"
          >
            <div className="fr-modal__header">
              <h2 id="fr-add-po-title">Add TotalEnergies PO</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="fr-soft-btn h-8 w-8"
                onClick={handleCloseAddPoModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="fr-modal__form">
              <p className="fr-modal__hint">
                Fill in the PO details below. Amount invoiced starts at 0; PO balance matches the PO amount until invoicing is recorded.
              </p>
              <div className="fr-field-row">
                <div className="fr-field">
                  <Label htmlFor="add-po-project-name">Project *</Label>
                  <select
                    id="add-po-project-name"
                    className="fr-modal-select"
                    value={addPoForm.projectName}
                    onChange={(e) => handleAddPoFormChange('projectName', e.target.value)}
                    required
                  >
                    <option value="">Select project</option>
                    {totalEnergiesPoProjectOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="fr-field">
                  <Label htmlFor="add-po-sd-no">SD No *</Label>
                  <Input
                    id="add-po-sd-no"
                    value={addPoForm.sdNo}
                    onChange={(e) => handleAddPoFormChange('sdNo', e.target.value)}
                    placeholder="e.g. SD-005"
                  />
                </div>
              </div>
              <div className="fr-field-row">
                <div className="fr-field">
                  <Label htmlFor="add-po-po-no">PO No *</Label>
                  <Input
                    id="add-po-po-no"
                    value={addPoForm.poNo}
                    onChange={(e) => handleAddPoFormChange('poNo', e.target.value)}
                    placeholder="e.g. PO-551001175"
                  />
                </div>
                <div className="fr-field">
                  <Label htmlFor="add-po-po-date">PO Date *</Label>
                  <Input
                    id="add-po-po-date"
                    type="date"
                    value={poDisplayDateToInputValue(addPoForm.poDate)}
                    onChange={(e) => handleAddPoFormChange('poDate', poInputValueToDisplayDate(e.target.value))}
                  />
                </div>
              </div>
              <div className="fr-field">
                <Label htmlFor="add-po-amount">PO Amount (TZS) *</Label>
                <Input
                  id="add-po-amount"
                  value={addPoForm.poAmount}
                  onChange={(e) => handleAddPoFormChange('poAmount', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="fr-balance-preview">
                <span className="fr-balance-preview__label">PO Balance Preview</span>
                <span className="fr-balance-preview__value">{formatTZS(addPoBalancePreview)}</span>
              </div>
              <div className="fr-modal__actions">
                <Button type="button" variant="ghost" className="fr-soft-btn" onClick={handleCloseAddPoModal}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSaveNewPo} disabled={savingPo}>
                  {savingPo ? 'Saving…' : 'Save PO'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showDepositEditModal && editingDeposit ? (
        <div className="fr-modal-overlay" onClick={handleCloseDepositEditModal} role="presentation">
          <div
            className="fr-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="fr-edit-deposit-title"
          >
            <div className="fr-modal__header">
              <h2 id="fr-edit-deposit-title">Edit Security Deposit Comment</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="fr-soft-btn h-8 w-8"
                onClick={handleCloseDepositEditModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="fr-modal__form">
              <div className="fr-field-row">
                <div className="fr-field">
                  <Label htmlFor="edit-deposit-company">Company</Label>
                  <Input id="edit-deposit-company" value={editingDeposit.company} readOnly className="fr-input-readonly" />
                </div>
                <div className="fr-field">
                  <Label htmlFor="edit-deposit-project">Project</Label>
                  <Input id="edit-deposit-project" value={editingDeposit.project} readOnly className="fr-input-readonly" />
                </div>
              </div>
              <div className="fr-field-row">
                <div className="fr-field">
                  <Label htmlFor="edit-deposit-amount">Amount Paid</Label>
                  <Input
                    id="edit-deposit-amount"
                    value={`TZS ${formatNumber(editingDeposit.amountPaid)}`}
                    readOnly
                    className="fr-input-readonly"
                  />
                </div>
                <div className="fr-field">
                  <Label htmlFor="edit-deposit-date">Date Paid</Label>
                  <Input id="edit-deposit-date" value={editingDeposit.datePaid} readOnly className="fr-input-readonly" />
                </div>
              </div>
              <div className="fr-field">
                <Label htmlFor="edit-deposit-comment">Comment *</Label>
                <textarea
                  id="edit-deposit-comment"
                  className="fr-modal-textarea"
                  value={depositEditForm.comment}
                  onChange={(e) => handleDepositEditFormChange('comment', e.target.value)}
                  placeholder="Enter comment..."
                  rows={4}
                  required
                />
              </div>
              <div className="fr-modal__actions">
                <Button type="button" variant="ghost" className="fr-soft-btn" onClick={handleCloseDepositEditModal}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSaveDeposit}>
                  Save Comment
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default FuelReport;
